import ScrollReveal from "@/components/base/ScrollReveal";
import { useNavigate } from "react-router-dom";

export default function AIGovernanceSection() {
  const navigate = useNavigate();
  const bullets = [
    "AI governance structure and operating model",
    "Trustworthy AI principles",
    "AI use case inventory approach",
    "Roles, responsibilities, and decision-making authorities",
    "Practical policy and process recommendations",
    "Risk identification and management approaches",
    "Compliance with regulations, standards, and guidance (e.g., EU AI Act, OMB Memos, ISO, NIST, etc.)",
    "Governance reviews for existing or proposed AI initiatives",
  ];

  const goToContactForm = () => {
    navigate("/contact", { state: { scrollTo: "start-conversation" } });
  };

  return (
    <section id="governance" className="relative bg-bg-section py-20 md:py-28 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/[0.03] to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute top-1/3 -left-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 -right-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                Flagship Advisory Offering
              </span>
              <h2 className="font-serif text-[32px] md:text-[44px] lg:text-[52px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                AI Governance Is No Longer{" "}
                <span className="italic text-primary">Optional</span>
              </h2>
              <p className="font-sans text-[17px] md:text-[19px] text-text-muted leading-relaxed max-w-3xl mx-auto">
                Organizations need clear principles, defined ownership, use case visibility, risk management, oversight processes, and practical policies that help teams innovate with AI responsibly.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <ScrollReveal direction="left">
              <div className="flex flex-col gap-6">
                <div className="relative group rounded-2xl overflow-hidden shadow-2xl bg-bg-card">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_1a0f7e90e26de7f71d8fbb02c1e1ca58_3b014855.jpg"
                    alt="Strategic AI governance meeting at enterprise boardroom table"
                    className="w-full h-auto object-contain object-center"
                  />
                </div>
                <div className="bg-bg-card rounded-2xl p-8 md:p-10 border border-border relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-primary-tint rounded-full flex items-center justify-center">
                        <i className="ri-shield-check-line text-primary text-xl w-6 h-6 flex items-center justify-center" />
                      </div>
                      <span className="font-sans text-[14px] font-semibold text-primary tracking-wider uppercase">
                        AI Governance Advisory
                      </span>
                    </div>
                    <p className="font-sans text-[16px] md:text-[17px] text-text-muted leading-[1.7] mb-6">
                      VantageSight helps organizations establish AI governance that is practical and aligned with their mission, operations, workforce, risk environment, and relevant compliance requirements.
                    </p>
                    <p className="font-serif text-[22px] md:text-[28px] text-primary italic leading-relaxed">
                      Governance that enables innovation, not blocks it.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={150}>
              <div className="flex flex-col gap-4">
                {bullets.map((bullet, i) => (
                  <ScrollReveal key={i} delay={200 + i * 80} direction="right">
                    <div className="flex items-start gap-4 group">
                      <div className="w-8 h-8 bg-primary-tint rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300 group-hover:bg-primary/15">
                        <i className="ri-check-line text-primary text-lg w-5 h-5 flex items-center justify-center" />
                      </div>
                      <p className="font-sans text-[15px] md:text-[16px] text-text-body leading-relaxed">
                        {bullet}
                      </p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={400}>
            <div className="text-center mt-14">
              <button
                onClick={goToContactForm}
                className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                Discuss How VantageSight Can Help
              </button>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}